import React, { useEffect, useRef, useState } from 'react';
import './QuotesTicker.css';

// MQL5 live quotes ticker.
//
// Injected imperatively rather than written as JSX, because React will not execute a <script>
// tag rendered from JSX or dangerouslySetInnerHTML. This widget also needs BOTH a `src` and an
// inline JSON body — the loaded script reads its own tag's text content for configuration — so
// the element has to be built by hand and the config assigned to textContent.
//
// The target div must already be in the DOM when the script runs, which is why injection
// happens in an effect (after the first paint) rather than during render.

const WIDGET_SRC = 'https://c.mql5.com/js/widgets/quotes/widget.js?v=3';

const WIDGET_CONFIG = {
  type: 'ticker',
  filter: ['EURUSD', 'USDJPY', 'GBPUSD', 'AUDUSD', 'USDCAD', 'USDCHF', 'NZDUSD'],
  width: '100%',
  height: 50,
  id: 'quotesWidgetTicker',
  fw: 'html',
  theme: 1,
};

function QuotesTicker() {
  const mountRef = useRef(null);
  const injectedRef = useRef(false);
  // This is a THIRD-PARTY script that loads from mql5.com and can set its own cookies. The site
  // tells users they may reject optional cookies, so honouring that has to happen here — a
  // consent banner that does not actually gate anything is worse than having none.
  const [consent, setConsent] = useState(() => {
    try { return localStorage.getItem('cookieConsent'); } catch (error) { return null; }
  });

  // The banner writes consent after this component has already mounted, so the value is polled
  // briefly rather than read once — otherwise accepting would not reveal the ticker until the
  // next page load.
  useEffect(() => {
    if (consent === 'accepted') return undefined;
    const timer = window.setInterval(() => {
      try {
        const current = localStorage.getItem('cookieConsent');
        if (current && current !== consent) setConsent(current);
      } catch (error) { /* storage unavailable — leave it gated */ }
    }, 1000);
    return () => window.clearInterval(timer);
  }, [consent]);

  useEffect(() => {
    if (consent !== 'accepted') return undefined;
    // StrictMode runs effects twice in development; without this the widget mounts twice and
    // renders a duplicated strip.
    if (injectedRef.current || !mountRef.current) return undefined;
    injectedRef.current = true;

    const script = document.createElement('script');
    script.async = true;
    script.type = 'text/javascript';
    script.setAttribute('data-type', 'quotes-widget');
    script.src = WIDGET_SRC;
    script.textContent = JSON.stringify(WIDGET_CONFIG);
    mountRef.current.appendChild(script);

    return undefined;
  }, [consent]);

  if (consent !== 'accepted') {
    return (
      <div className="quotes-ticker-shell is-gated">
        <p>
          Live market quotes are provided by MetaQuotes and load only once optional cookies are
          accepted.
        </p>
      </div>
    );
  }

  return (
    <div className="quotes-ticker-shell" ref={mountRef}>
      <div id="quotesWidgetTicker" />
      <div className="qw-copyright">
        <a
          href="https://www.metatrader.com/?utm_source=quotes.widget&utm_medium=link&utm_term=quotes.ticker&utm_content=visit.mql5.quotes&utm_campaign=quotes.ticker.widget"
          rel="noopener noreferrer nofollow"
          target="_blank"
        >
          MetaTrader World Markets
        </a>
      </div>
    </div>
  );
}

export default QuotesTicker;
